import PropTypes from 'prop-types'
import { push } from '@lagunovsky/redux-react-router'
import { flatten, groupBy, intersectionWith, isNil, isUndefined, orderBy, uniq, uniqBy } from 'lodash'
import { ANTENNA_ID, CENTRAL_ID, EQUIPMENT_ID, LIST_PANEL, MAP_PANEL, MAT_ANTENNA, MAT_CENTRAL, MAT_DIVERS, MAT_EQUIPMENT, MAT_HEADERS_INVENTORY, MAT_POWER_SUPPLY, MAT_SENSOR, MAT_SIM, MAT_SUBSCRIPTION, MAT_TELECOM, MATERIEL_TYPES, POWER_SUPPLY_ID, SENSOR_ID, SIM_ID, SUBSCRIPTION_ID, TELECOM_ID, VARIOUS_ID } from 'materiel/constants/MaterielConstants'
import React, { useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { nbPerPageLabel } from 'referencial/constants/ReferencialConstants'
import i18n from 'simple-react-i18n'
import { STATION_LIST, STATION_TYPE_CONSTANT } from 'station/constants/StationConstants'
import { hasValue } from 'utils/NumberUtil'
import { H_MAT_INVENTORY } from '../../../account/constants/AccessRulesConstants'
import MaterielAssignmentLink, { getStationLink } from '../MaterielAssignmentLink'
import ProgressCard from '../../../components/card/ProgressCard'
import Checkbox from '../../../components/forms/Checkbox'
import { getDate, getFullDate } from '../../../utils/DateUtil'
import { componentHasHabilitations } from '../../../utils/HabilitationUtil'
import { createIconMaterialState, getAssignmentLabel, getMaterielIconValue } from '../../../utils/MaterielUtils'
import { getLabel } from '../../../utils/StoreUtils'
import { searchAllCharacters } from '../../../utils/StringUtil'
import MaterielAction from '../../actions/MaterielAction'
import { hasMaterielTypeHabilitation } from '../../utils/MaterielUtils'
import MaterielsMapComponent from '../map/MaterielsMapComponent'
import useActions from 'utils/customHook/useActions'
import { exportFile, getDataWithHeaders } from 'utils/ExportDataUtil'
import SimpleTabList from 'components/list/SimpleTabList'
import useLocalStorage from '../../../utils/customHook/useLocalStorage'
import { TELECOM } from '../../constants/MaterielConstants'
import { Button, Card, CardContent, Grid, Icon, Stack, Tooltip, Checkbox as CheckboxMUI } from '@mui/material'
import Input from 'components/forms/Input'
import Select from 'components/forms/Select'
import CampaignSelect from 'components/forms/specific/CampaignSelect'
import SuperMultiAutocomplete from 'components/forms/SuperMultiAutocomplete'
import MultiContributorsAutocomplete from 'referencial/components/contributor/components/MultiContributorsAutocomplete'
import useTitle from 'utils/customHook/useTitle'
import CentralAction from '../central/actions/CentralAction'
import SensorAction from '../sensor/actions/SensorAction'
import ContributorAction from 'referencial/components/contributor/actions/ContributorAction'
import PowerSupplyAction from '../powerSupply/actions/PowerSupplyAction'
import SimAction from '../sim/actions/SimAction'
import VariousMaterielAction from '../variousMateriel/actions/VariousMaterielAction'
import TelecomAction from '../telecom/actions/TelecomAction'
import EquipmentAction from '../equipment/actions/EquipmentAction'
import SubscriptionAction from '../subscription/actions/SubscriptionAction'
import AntennaAction from '../antenna/actions/AntennaAction'
import CardToMuchData from 'components/card/CardToMuchData'
import { StyledFieldSet } from 'components/StyledElements'
import JobAction from 'import/actions/JobAction'
import RefJobAction from 'domain/job/RefJobAction'
import DtoJobLogLight from 'import/dto/DtoJobLogLight'
import useUpdateEffect from 'utils/customHook/useUpdateEffect'
import ToastrAction from 'toastr/actions/ToastrAction'
import useBoolean from 'utils/customHook/useBoolean'
import { promiseAllProgress } from 'utils/ActionUtils'
import NumberField from 'components/forms/NumberField'
import useParseJson from 'utils/customHook/useParseJson'
import { ALIGN, CardTable, NewTable, SORT } from 'components/datatable/NewTable'

const MAX_ELEMENTS = 5000

const getcolorLog = (status) => {
    if (status === 'success') {
        return 'green-200'
    } else if (status === 'error') {
        return 'red-200'
    } else if (status === 'warning') {
        return 'yellow-200'
    }
    return undefined
}

const DEFAULT_FILTER = {
    administrator: undefined,
    state: undefined,
    manufacturer: undefined,
    network: undefined,
    matType: undefined,
    searchValue: '',
    displayEquipment: false,
    displayOutOfService: false,
    stations: [],
}

const InventoryFilterForm = ({
    materiels = [],
    typeList,
    onValidate = () => { },
    materielType = '',
}) => {
    const {
        materielStates,
        contributors,
        networks,
        materielTypes,
    } = useSelector(store => ({
        contributors: store.ContributorReducer.contributors,
        materielStates: store.MaterielReducer.materielStates,
        networks: store.NetworkReducer.networks,
        materielTypes: store.MaterielReducer.materielTypes,
    }), shallowEqual)

    const authorizedMaterielTypes = useMemo(() => materielTypes.filter(({ id }) => hasMaterielTypeHabilitation(id)), [materielTypes])

    const materielsFiltered = materiels.filter(m => m.assignment).map((m, index) => ({ id: index + 1, name: m.assignment }))
    const materielsStations = uniqBy(materielsFiltered, 'name')

    const [administrator, setAdministrator] = useLocalStorage(`${materielType}_ADMINISTRATOR`, DEFAULT_FILTER.administrator)
    const [state, setState] = useLocalStorage(`${materielType}_STATE`, DEFAULT_FILTER.state)
    const [displayOutOfService, setDisplayOutOfService] = useLocalStorage(`${materielType}_DISPLAY_OUT_OF_SERVICE`, DEFAULT_FILTER.displayOutOfService)
    const [manufacturer, setManufacturer] = useLocalStorage(`${materielType}_MANUFACTURER`, DEFAULT_FILTER.manufacturer)
    const [network, setNetwork] = useLocalStorage(`${materielType}_NETWORK`, DEFAULT_FILTER.network)
    const [matType, setMatType] = useLocalStorage(`${materielType}_TYPE`, DEFAULT_FILTER.matType)
    const [searchValue, setSearchValue] = useLocalStorage(`${materielType}_SEARCH_VALUE`, DEFAULT_FILTER.searchValue)
    const [displayEquipment, setDisplayEquipment] = useLocalStorage(`${materielType}_DISPLAY_EQUIPMENT`, DEFAULT_FILTER.displayEquipment)
    const [stations, setStations] = useLocalStorage(`${materielType}_STATION_NAMES`, DEFAULT_FILTER.stations)
    const [materielTypesSelected, setMaterielTypesSelected] = useLocalStorage(`${materielType}_MATERIEL_TYPES`, () => authorizedMaterielTypes.map(({ id }) => id))

    const stationIds = materielsStations.filter(m => stations.includes(m.name)).map(m => m.id)
    const onChangeStations = ids => setStations(materielsStations.filter(m => ids.includes(m.id)).map(m => m.name))

    const getFilter = availableMaterielTypes => ({
        administrator,
        state,
        manufacturer,
        network,
        matType,
        searchValue,
        displayEquipment,
        displayOutOfService,
        stations,
        availableMaterielTypes,
    })

    useEffect(() => {
        if (!authorizedMaterielTypes.length) return
        const authorizedIds = authorizedMaterielTypes.map(({ id }) => id)
        const typesSelected = materielTypesSelected.filter(id => authorizedIds.includes(id))
        setMaterielTypesSelected(typesSelected)
        onValidate(getFilter(typesSelected))
    }, [authorizedMaterielTypes])

    const onReset = () => {
        const authorizedIds = authorizedMaterielTypes.map(({ id }) => id)
        setAdministrator(DEFAULT_FILTER.administrator)
        setState(DEFAULT_FILTER.state)
        setDisplayOutOfService(DEFAULT_FILTER.displayOutOfService)
        setManufacturer(DEFAULT_FILTER.manufacturer)
        setNetwork(DEFAULT_FILTER.network)
        setMatType(DEFAULT_FILTER.matType)
        setSearchValue(DEFAULT_FILTER.searchValue)
        setDisplayEquipment(DEFAULT_FILTER.displayEquipment)
        setStations(DEFAULT_FILTER.stations)
        setMaterielTypesSelected(authorizedIds)
        onValidate({ ...DEFAULT_FILTER, availableMaterielTypes: authorizedIds })
    }

    const stateCodes = uniq(materiels.map(m => m.statusCode)).filter(code => displayOutOfService || (code !== 0 && code !== 5 && code !== 6))
    const filteredStates = stateCodes.map(code => materielStates.find(c => c.code === code)).filter(s => !!s)

    const manufacturerIds = uniq(materiels.map(m => m.manufacturerId))
    const filteredManufacturers = manufacturerIds.map(id => contributors.find(c => c.id === id)).filter(c => !!c)

    const administratorIds = uniq(materiels.map(m => m.administratorId))
    const filteredAdministrators = administratorIds.map(id => contributors.find(c => c.id === id)).filter(c => !!c)

    const networkIds = uniq(materiels.map(m => m.networkCode))
    const filteredNetworks = networkIds.map(id => networks.find(c => c.id === id)).filter(n => !!n)

    const materielTelecom = authorizedMaterielTypes.find(m => m.code === TELECOM)

    const changeSelectedMateriel = (id) => {
        setMaterielTypesSelected(prev => {
            if (prev.includes(id)) return prev.filter(t => t !== id)
            return [...prev, id]
        })
    }

    useTitle(() => [
        {
            title: i18n.materiels,
            href: 'materiel',
        },
        {
            title: i18n.inventory,
            href: 'materiel/inventory',
        },
    ], [])

    return (
        <Card>
            <CardContent>
                <Grid container spacing={1} alignItems='center'>
                    <Grid container size={12} justifyContent='space-between'>
                        <Grid size='auto'>
                            <Checkbox
                                label={i18n.displayEquipments}
                                checked={displayEquipment}
                                onChange={setDisplayEquipment}
                            />
                        </Grid>
                        {authorizedMaterielTypes.filter(m => m.code !== TELECOM).map((type, i) => (
                            <Grid key={i} size='auto'>
                                <Checkbox
                                    label={type.label}
                                    onChange={(e) => changeSelectedMateriel(type.id, e)}
                                    checked={materielTypesSelected.some(id => id === type.id)}
                                />
                            </Grid>
                        ))}
                        {!!materielTelecom?.id && (
                            <Grid size='auto'>
                                <Checkbox
                                    label={materielTelecom.label}
                                    onChange={(e) => changeSelectedMateriel(materielTelecom.id, e)}
                                    checked={materielTypesSelected.includes(materielTelecom.id)}
                                />
                            </Grid>
                        )}
                    </Grid>
                    <Grid size={4}>
                        <Input
                            title={i18n.search}
                            value={searchValue}
                            onChange={setSearchValue}
                            onEnterKeyPress={()=> onValidate(getFilter(materielTypesSelected))}
                        />
                    </Grid>
                    <Grid size={4}>
                        <Select
                            label={i18n.state}
                            options={filteredStates}
                            value={state}
                            onChange={setState}
                            keyLabel='label'
                            keyValue='code'
                            integerValue
                            returnNull
                        />
                    </Grid>
                    <Grid size={4}>
                        <Checkbox
                            componentClassName='padding-top-1'
                            label={i18n.displayOutOfService}
                            tooltip={i18n.displayOutOfService}
                            checked={displayOutOfService}
                            onChange={v => {
                                setDisplayOutOfService(v)
                                if (!v && (state === 0 || state === 5 || state === 6)) {
                                    setState(undefined)
                                }
                            }}
                        />
                    </Grid>
                    <Grid size={4}>
                        <Select
                            label={i18n.network}
                            options={filteredNetworks}
                            value={network}
                            onChange={setNetwork}
                            keyLabel='name'
                            keyValue='id'
                            integerValue
                            returnNull
                        />
                    </Grid>
                    <Grid size={4}>
                        <CampaignSelect
                            changeFilter={() => { }}
                        />
                    </Grid>
                    <Grid size={4}>
                        <SuperMultiAutocomplete
                            label={i18n.stations}
                            options={materielsStations}
                            keyLabel='name'
                            keyValue='id'
                            onChange={onChangeStations}
                            values={stationIds}
                            multiple
                        />
                    </Grid>
                    <Grid size={typeList ? 3 : 4}>
                        <MultiContributorsAutocomplete
                            label={i18n.administrator}
                            options={filteredAdministrators}
                            values={administrator}
                            onChange={setAdministrator}
                            keyLabel='labelDisplay'
                            keyValue='id'
                        />
                    </Grid>
                    <Grid size={typeList ? 3 : 4}>
                        <MultiContributorsAutocomplete
                            label={i18n.manufacturer}
                            options={filteredManufacturers}
                            values={manufacturer}
                            onChange={setManufacturer}
                            keyLabel='labelDisplay'
                            keyValue='id'
                        />
                    </Grid>

                    {!!typeList && (
                        <Grid size={2}>
                            <Select
                                label={i18n.materielTypes}
                                options={typeList}
                                value={matType}
                                onChange={setMatType}
                                keyLabel='label'
                                keyValue='id'
                                integerValue
                                returnNull
                            />
                        </Grid>
                    )}
                    <Grid size={2}>
                        <Button
                            variant='outlined'
                            onClick={onReset}
                            fullWidth
                        >
                            {i18n.reinit}
                        </Button>
                    </Grid>
                    <Grid size={2}>
                        <Button
                            variant='contained'
                            onClick={() => onValidate(getFilter(materielTypesSelected))}
                            fullWidth
                        >
                            {i18n.search}
                        </Button>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    )
}

InventoryFilterForm.propTypes = {
    onValidate: PropTypes.func,
    changeView: PropTypes.func,
    changeSelectedMateriel: PropTypes.func,
    materiels: PropTypes.arrayOf(PropTypes.shape({
        statusCode: PropTypes.number,
        manufacturerId: PropTypes.number,
        administratorId: PropTypes.number,
        networkCode: PropTypes.number,
    })),
    typeList: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number,
        label: PropTypes.string,
    })),
    materielTypesSelected: PropTypes.arrayOf(PropTypes.number),
    view: PropTypes.string,
    materielType: PropTypes.string,
}

const JobContent = ({
    job = {},
    setJob = () => {},

    logs = [],
}) => {
    const dispatch = useDispatch()
    const {
        accountUser,
        contributors,
    } = useSelector(store => ({
        accountUser: store.AccountReducer.accountUser,
        contributors: store.ContributorReducer.contributors,
    }), shallowEqual)

    const {
        json: filters = {},
    } = useParseJson(job.parameters?.filters?.[0] ?? '{}')

    const [fileName, setFileName] = useState()

    const formattedLogs = useMemo(() => {
        const formatted = logs.map(l => ({
            ...l,
            color: getcolorLog(l.status),
            date: getFullDate(l.date),
        }))
        return orderBy(formatted, 'id', 'desc')
    }, [logs])

    const onChange = (obj = {}) => setJob(pj => ({
        ...pj,
        parameters: {
            ...pj.parameters,
            filters: [JSON.stringify({ ...filters, ...obj })],
        },
    }))

    useEffect(() => {
        onChange({ validator: accountUser.contributorCode })
    }, [])

    const openFile = e => {
        const reader = new FileReader()
        const fileOpened = e.target.files[0]

        if (isUndefined(fileOpened)) {
            return
        }

        reader.onload = upload => {
            const split = upload.target.result.split(',')

            if (split[0].indexOf('base64') > 0) {
                setFileName(fileOpened.name)
                onChange({ file: split[1], nameOfFile: fileOpened.name })
            } else {
                dispatch(ToastrAction.error(i18n.theSelectedFileMustBeInCsvFormat))
            }
        }
        reader.readAsDataURL(fileOpened)
    }

    return (
        <>
            <StyledFieldSet>
                <Grid container spacing={1} sx={{ padding: '10' }} alignItems='center'>
                    <Grid size={2}>
                        <Button
                            variant='contained'
                            color='primary'
                            component='label'
                            fullWidth
                        >
                            <span>{i18n.browse}</span>
                            <input
                                type='file'
                                accept='.xlsx, .XLSX'
                                hidden
                                onChange={openFile}
                            />
                        </Button>
                    </Grid>
                    <Grid size={10}>
                        <label style={{ fontSize: '1em' }}>{fileName ?? i18n.pleaseSelectFile}</label>
                    </Grid>
                    <Grid size={12}>
                        <MultiContributorsAutocomplete
                            label={i18n.regionalDirection}
                            values={filters.validator}
                            onChange={v => onChange({ validator: v })}
                            options={contributors}
                            disabled={accountUser.isAdmin !== '1'}
                        />
                    </Grid>
                    <Grid size={6}>
                        <Checkbox
                            label={i18n.hasHeader}
                            onChange={v => onChange({ hasHeader: v })}
                            checked={filters.hasHeader}
                        />
                    </Grid>
                    <Grid size={6}>
                        <NumberField
                            title={i18n.nbLine}
                            value={filters.nbHeaders ?? 6}
                            onChange={v => onChange({ nbHeaders: v })}
                            floatValue
                        />
                    </Grid>
                </Grid>
            </StyledFieldSet>
            {formattedLogs.length !== 0 && (
                <div style={{ marginTop: '10px' }}>
                    <NewTable
                        rows={formattedLogs}
                        headers={['date', { key: 'value', value: i18n.valueLabel }, 'status']}
                        maxHeight='62vh'

                        defaultSort={{ column: 'date', direction: SORT.DESC }}
                    />
                </div>
            )}
        </>
    )
}

JobContent.propTypes = {
    job: PropTypes.shape({}),
    setJob: PropTypes.func,

    logs: PropTypes.arrayOf(PropTypes.shape({})),
}

const ActionComponent = ({ // just to optimize
    exportData = [],
    materialHeaders = [],
}) => {
    const dispatch = useDispatch()
    const {
        jobs,
    } = useSelector(store => ({
        jobs: store.JobReducer.jobs,
    }), shallowEqual)

    const getJob = () => {
        const job = jobs.find(j => j.id === -19)
        if (isNil(job)) return {}

        return {
            ...job,
            parameters: JSON.parse(job.parameters ?? '{}'),
        }
    }

    const [job, setJob] = useState(getJob) // manual job

    const [lastExecution, setlastExecution] = useState()
    const [logs, setLogs] = useState([])

    const getLogs = () => {
        RefJobAction.promiseJobExecutions(job.id, 1).then(jobExecutions => {
            const [lastExec] = jobExecutions

            if (isNil(lastExec)) return

            JobAction.requestJobLogs(job.id, lastExec.id).then(logsJob => {
                const jobLogs = logsJob.data.map(log => new DtoJobLogLight(log, lastExec.id))
                setLogs(jobLogs)
                setlastExecution(lastExec)
            })
        })
    }

    useUpdateEffect(() => {
        if (isNil(lastExecution) || !isNil(lastExecution.statusCode)) {
            dispatch(ToastrAction.info(i18n.treatmentFinishReload))
            return
        }

        setTimeout(getLogs, 2000)
    }, [lastExecution])

    useEffect(() => {
        if (jobs.length === 0) {
            dispatch(JobAction.fetchJobs())
        }
    }, [])

    useEffect(() => {
        if (job.id !== -19 && jobs.length !== 0) {
            setJob(getJob)
        }
    }, [job.id, jobs])

    const onSave = () => {
        dispatch(JobAction.updateJob(job, 10)).then(() => {
            dispatch(JobAction.executeJob(job.id, 10)).then(getLogs)
        })
    }

    useActions(() => {
        return {
            referencialActions: {
                actions: {
                    export: {
                        data: getDataWithHeaders(exportData, materialHeaders),
                        titleFile: i18n.materiels,
                    },
                    importMat: {
                        icon: 'upload',
                        title: i18n.importMatAccounting,
                        description: 'Cette action vous permet d\'importer un fichier formaté correspondant à l\'import comptable.',
                        steps: [
                            { label: i18n.setting, constant: 1 },
                        ],
                        componentStep1: (
                            <JobContent
                                job={job}
                                setJob={setJob}

                                logs={logs}
                            />
                        ),
                        validateButton: () => (
                            <Button
                                onClick={onSave}
                                disabled={!isNil(lastExecution) && isNil(lastExecution?.statusCode)}
                                variant='contained'
                            >
                                {i18n.execute}
                            </Button>
                        ),
                    },
                },
            },
        }
    }, [exportData, job, lastExecution, logs])

    return null
}

ActionComponent.propTypes = {
    exportData: PropTypes.arrayOf(PropTypes.shape({})),
    materialHeaders: PropTypes.arrayOf(PropTypes.string),
}

const MAT_TYPE_HEADERS = [MAT_CENTRAL, MAT_SENSOR, MAT_POWER_SUPPLY, MAT_TELECOM, MAT_SIM, MAT_DIVERS, MAT_EQUIPMENT, MAT_SUBSCRIPTION, MAT_ANTENNA]

const MAT_TYPE_ID_BY_LINK = Object.fromEntries(MATERIEL_TYPES.map(({ link, id }) => [link, id]))

const getStationHeaders = availableMaterielTypes => {
    const authorizedHeaders = MAT_TYPE_HEADERS.filter(matType => hasMaterielTypeHabilitation(MAT_TYPE_ID_BY_LINK[matType]))
    const typeHeaders = isNil(availableMaterielTypes)
        ? authorizedHeaders
        : authorizedHeaders.filter(matType => availableMaterielTypes.includes(MAT_TYPE_ID_BY_LINK[matType]))
    return { typeHeaders, headers: ['assignment', ...typeHeaders] }
}

const groupMaterielsByStation = materiels => {
    const grouped = groupBy(materiels.filter(mat => hasValue(mat.assignment)), mat => `${mat.siteType}-${mat.siteCode}`)
    const stations = Object.values(grouped).map(materielsOfStation => {
        const [{ assignment, statusCode, siteType, siteCode }] = materielsOfStation
        return { assignment, statusCode, siteType, siteCode, materiels: materielsOfStation }
    })
    return orderBy(stations, ['siteType', 'assignment'])
}

const getStationTypeCells = (station, typeHeaders, buildCell) => Object.fromEntries(
    typeHeaders.map(matType => [matType, buildCell(station.materiels.filter(mat => mat.materielCategory === matType))]),
)

const getMaterielLabel = ({ type, serialNumber, immoNum }) => [type, serialNumber || immoNum].filter(v => !!v).join(' - ') || i18n.notDefined

const StationListApp = ({
    materiels = [],
    availableMaterielTypes,
}) => {
    const dispatch = useDispatch()

    const stations = useMemo(() => groupMaterielsByStation(materiels), [materiels])

    const { typeHeaders, headers } = useMemo(() => getStationHeaders(availableMaterielTypes), [availableMaterielTypes])

    const rows = useMemo(() => stations.map(station => ({
        ...station,
        assignment: <MaterielAssignmentLink materiel={station} />,
        ...getStationTypeCells(station, typeHeaders, matchingMateriels => ({
            sortValue: !!matchingMateriels.length,
            value: !!matchingMateriels.length && (
                <Tooltip
                    title={(
                        <Stack>
                            {matchingMateriels.map(mat => <span key={mat.link}>{getMaterielLabel(mat)}</span>)}
                        </Stack>
                    )}
                >
                    <span>
                        <CheckboxMUI sx={{ padding: '5px' }} checked disabled />
                    </span>
                </Tooltip>
            ) || '',
            align: ALIGN.CENTER,
        })),
    })), [stations, typeHeaders])

    const exportData = useMemo(() => stations.map(station => ({
        assignment: station.assignment,
        ...getStationTypeCells(station, typeHeaders, matchingMateriels => ({
            value: matchingMateriels.length,
            format: '0',
            cellType: 'number',
        })),
    })), [stations, typeHeaders])

    const actions = [
        {
            icon: 'file_download',
            tooltip: i18n.export,
            onClick: () => exportFile({
                data: getDataWithHeaders(exportData, headers),
                exportType: 'xlsx',
                titleFile: i18n.stations,
            }),
        },
    ]

    return (
        <CardTable
            title={i18n.stations}
            actions={actions}
            rows={rows}
            headers={headers}
            rowsPerPageOptions={nbPerPageLabel}
            onClickRow={({ siteType, siteCode }) => {
                const link = getStationLink(siteType, siteCode)
                return link && dispatch(push(link))
            }}
        />
    )
}

StationListApp.propTypes = {
    materiels: PropTypes.array,
    availableMaterielTypes: PropTypes.arrayOf(PropTypes.number),
}

const MaterialListApp = ({
    materiels = [],
    exportData = [],
}) => {
    const dispatch = useDispatch()
    const materialHeaders = ['materielCategory', ...MAT_HEADERS_INVENTORY]

    return (
        <>
            <CardTable
                title={i18n.materiels}
                rows={materiels}
                headers={['nullValue', ...materialHeaders]}
                rowsPerPageOptions={nbPerPageLabel}
                onClickRow={({ link }) => dispatch(push(link))}
            />
            <ActionComponent
                exportData={exportData}
                materialHeaders={materialHeaders}
            />
        </>
    )
}

MaterialListApp.propTypes = {
    materiels: PropTypes.array,
    exportData: PropTypes.array,
}

const InventoryApp = () => {
    const {
        piezometers,
        qualitometers,
        hydrometricStations,
        pluviometers,
        installations,
        materielStates,
        centrals,
        centralsLastSituations,
        centralTypes,
        powerSupplies,
        powerSuppliesLastSituations,
        powerSupplyTypes,
        sensors,
        sensorsLastSituations,
        sensorTypes,
        variousMateriels,
        variousMaterielsLastSituations,
        variousMaterielTypes,
        equipments,
        equipmentTypes,
        equipmentsLastSituations,
        sims,
        simTypes,
        simsLastSituations,
        telecoms,
        telecomTypes,
        telecomsLastSituations,
        subscriptions,
        subscriptionTypes,
        subscriptionsLastSituations,
        antennas,
        antennasLastSituations,
        antennaTypes,
        contributors,
        materielTypes,
    } = useSelector(store => ({
        piezometers: store.PiezometryReducer.piezometersLight,
        qualitometers: store.QualityReducer.qualitometersLight,
        hydrometricStations: store.HydrometryReducer.hydrometricStations,
        pluviometers: store.PluviometryReducer.pluviometers,
        installations: store.InstallationReducer.installations,
        materielStates: store.MaterielReducer.materielStates,
        centrals: store.CentralReducer.centrals,
        centralsLastSituations: store.CentralReducer.centralsLastSituations,
        centralTypes: store.CentralReducer.centralTypes,
        powerSupplies: store.PowerSupplyReducer.powerSupplies,
        powerSuppliesLastSituations: store.PowerSupplyReducer.powerSuppliesLastSituations,
        powerSupplyTypes: store.PowerSupplyReducer.powerSupplyTypes,
        sensors: store.SensorReducer.sensors,
        sensorsLastSituations: store.SensorReducer.sensorsLastSituations,
        sensorTypes: store.SensorReducer.sensorTypes,
        variousMateriels: store.VariousMaterielReducer.variousMateriels,
        variousMaterielsLastSituations: store.VariousMaterielReducer.variousMaterielsLastSituations,
        variousMaterielTypes: store.VariousMaterielReducer.variousMaterielTypes,
        equipments: store.EquipmentReducer.equipments,
        equipmentTypes: store.EquipmentReducer.equipmentTypes,
        equipmentsLastSituations: store.EquipmentReducer.equipmentsLastSituations,
        sims: store.SimReducer.sims,
        simTypes: store.SimReducer.simTypes,
        simsLastSituations: store.SimReducer.simsLastSituations,
        telecoms: store.TelecomReducer.telecoms,
        telecomTypes: store.TelecomReducer.telecomTypes,
        telecomsLastSituations: store.TelecomReducer.telecomsLastSituations,
        subscriptions: store.SubscriptionReducer.subscriptions,
        subscriptionTypes: store.SubscriptionReducer.subscriptionTypes,
        subscriptionsLastSituations: store.SubscriptionReducer.subscriptionsLastSituations,
        antennas: store.AntennaReducer.antennas,
        antennasLastSituations: store.AntennaReducer.antennasLastSituations,
        antennaTypes: store.AntennaReducer.antennaTypes,
        contributors: store.ContributorReducer.contributors,
        materielTypes: store.MaterielReducer.materielTypes,
    }), shallowEqual)

    const [filter, setFilter] = useState({})
    const [view, setView] = useState(LIST_PANEL)
    const [showAll, setShowAll] = useState(false)

    const authorizedMaterielTypes = useMemo(() => materielTypes.filter(({ id }) => hasMaterielTypeHabilitation(id)), [materielTypes])

    const dispatch = useDispatch()

    useEffect(() => {
        if (!componentHasHabilitations(H_MAT_INVENTORY)) {
            dispatch(push('/unauthorized'))
        }
    }, [])

    useActions(() => [], [])

    const { value: dataload, setTrue: setLoaded, setFalse: setNotLoaded } = useBoolean(false)
    const [progress, setProgress] = useState(0)

    useEffect(() => {
        if (authorizedMaterielTypes.length === 0) return

        setNotLoaded()

        const promisesGeneral = [MaterielAction.fetchMaterielStates, ContributorAction.fetchContributors]
        const promisesCentral = authorizedMaterielTypes.some(t => t.id === CENTRAL_ID) ? [
            () => CentralAction.fetchCentrals(true),
            CentralAction.fetchCentralsLastSituations,
            CentralAction.fetchCentralTypes,
        ] : []
        const promisesSensor = authorizedMaterielTypes.some(t => t.id === SENSOR_ID) ? [
            () => SensorAction.fetchSensors(true),
            SensorAction.fetchSensorsLastSituations,
            SensorAction.fetchSensorTypes,
        ] : []
        const promisesPowerSupply = authorizedMaterielTypes.some(t => t.id === POWER_SUPPLY_ID) ? [
            () => PowerSupplyAction.fetchPowerSupplies(true),
            PowerSupplyAction.fetchPowerSuppliesLastSituations,
            PowerSupplyAction.fetchPowerSupplyTypes,
        ] : []
        const promisesSim = authorizedMaterielTypes.some(t => t.id === SIM_ID) ? [
            () => SimAction.fetchSims(true),
            SimAction.fetchSimsLastSituations,
            SimAction.fetchSimTypes,
        ] : []
        const promisesVariousMateriel = authorizedMaterielTypes.some(t => t.id === VARIOUS_ID) ? [
            VariousMaterielAction.fetchVariousMateriels,
            VariousMaterielAction.fetchVariousMaterielsLastSituations,
            VariousMaterielAction.fetchVariousMaterielTypes,
        ] : []
        const promisesTelecom = authorizedMaterielTypes.some(t => t.id === TELECOM_ID) ? [
            () => TelecomAction.fetchTelecoms(true),
            TelecomAction.fetchTelecomsLastSituations,
            TelecomAction.fetchTelecomTypes,
        ] : []
        const promisesEquipment = authorizedMaterielTypes.some(t => t.id === EQUIPMENT_ID) ? [
            EquipmentAction.fetchEquipments,
            EquipmentAction.fetchEquipmentsLastSituations,
            EquipmentAction.fetchEquipmentTypes,
        ] : []
        const promisesSubscription = authorizedMaterielTypes.some(t => t.id === SUBSCRIPTION_ID) ? [
            SubscriptionAction.fetchSubscriptions,
            SubscriptionAction.fetchSubscriptionsLastSituations,
            SubscriptionAction.fetchSubscriptionTypes,
        ] : []
        const promisesAntenna = authorizedMaterielTypes.some(t => t.id === ANTENNA_ID) ? [
            AntennaAction.fetchAntennas,
            AntennaAction.fetchAntennasLastSituations,
            AntennaAction.fetchAntennaTypes,
        ] : []

        promiseAllProgress([
            ...promisesGeneral,
            ...promisesCentral,
            ...promisesSensor,
            ...promisesPowerSupply,
            ...promisesSim,
            ...promisesVariousMateriel,
            ...promisesTelecom,
            ...promisesEquipment,
            ...promisesSubscription,
            ...promisesAntenna,
        ].map(p => dispatch(p())), setProgress).finally(setLoaded)
    }, [dispatch, authorizedMaterielTypes])

    const getCorrespondingSite = ({ siteType, siteCode }) => {
        switch (siteType) {
            case STATION_TYPE_CONSTANT.PIEZOMETRY:
                return piezometers.find(({ id }) => id === siteCode)
            case STATION_TYPE_CONSTANT.PLUVIOMETRY:
                return pluviometers.find(({ id }) => id === siteCode)
            case STATION_TYPE_CONSTANT.QUALITY:
                return qualitometers.find(({ id }) => id === siteCode)
            case STATION_TYPE_CONSTANT.HYDROMETRY:
                return hydrometricStations.find(({ id }) => id === siteCode)
            case STATION_TYPE_CONSTANT.INSTALLATION:
                return installations.find(({ id }) => id === siteCode)
            default:
                return {}
        }
    }

    const formatMateriel = (materiel, listOfLastSituations, listOfTypes, materielKey, materielTypeKey) => {
        const lastSituation = listOfLastSituations.find(o => o[materielKey] === materiel.id)
        const labelState = lastSituation ? getLabel(materielStates, lastSituation.statusCode) : ''
        const site = lastSituation && lastSituation.statusCode === 1 && lastSituation.siteType && lastSituation.siteCode && getCorrespondingSite(lastSituation) || {}
        const contributorsFiltered = contributors.filter(c => [materiel.manufacturerId, materiel.administrator].includes(c.id))
        const labelAssignment = (lastSituation && lastSituation.statusCode === 1 && getAssignmentLabel(site, lastSituation.siteName)) || ''
        return {
            type: getLabel(listOfTypes, materiel[materielTypeKey]),
            manufacturer: getLabel(contributorsFiltered, materiel.manufacturerId, 'labelDisplay', 'id'),
            manufacturerId: materiel.manufacturerId,
            purchaseDate: getDate(materiel.purchaseDate),
            stateDate: lastSituation ? getDate(lastSituation.situationDate) : '',
            statusCode: lastSituation?.statusCode,
            serialNumber: materiel.imei || materiel.serialNumber,
            immoNum: materiel.reference,
            administrator: getLabel(contributorsFiltered, materiel.administrator, 'labelDisplay', 'code'),
            administratorId: materiel.administrator,
            networkCode: materiel.networkCode,
            isEquipment: materiel.isEquipment,
            siteType: lastSituation?.siteType,
            siteCode: lastSituation?.siteCode,
            state: labelState,
            assignment: labelAssignment,
        }
    }

    const centralValue = useMemo(() => ({
        id: 1,
        getData: () => centrals.map(mat => ({
            ...formatMateriel(mat, centralsLastSituations, centralTypes, 'idCentral', 'centralType'),
            link: `/materiel/central/${mat.id}`,
            materielCategory: MAT_CENTRAL,
        })),
    }), [centralTypes, centrals, centralsLastSituations])

    const sensorsValue = useMemo(() => ({
        id: 2,
        getData: () => sensors.map(mat => ({
            ...formatMateriel(mat, sensorsLastSituations, sensorTypes, 'idSensor', 'sensorType'),
            link: `/materiel/sensor/${mat.id}`,
            materielCategory: MAT_SENSOR,
        })),
    }), [sensorTypes, sensors, sensorsLastSituations])

    const powerSuppliesValue = useMemo(() => ({
        id: 3,
        getData: () => powerSupplies.map(mat => ({
            ...formatMateriel(mat, powerSuppliesLastSituations, powerSupplyTypes, 'idPowerSupply', 'powerSupplyType'),
            link: `/materiel/powerSupply/${mat.id}`,
            materielCategory: MAT_POWER_SUPPLY,
        })),
    }), [powerSupplies, powerSuppliesLastSituations, powerSupplyTypes])

    const simsValue = useMemo(() => ({
        id: 4,
        getData: () => sims.map(mat => ({
            ...formatMateriel(mat, simsLastSituations, simTypes, 'idSim', 'simType'),
            link: `/materiel/sim/${mat.id}`,
            materielCategory: MAT_SIM,
        })),
    }), [simTypes, sims, simsLastSituations])

    const variousMaterielsValue = useMemo(() => ({
        id: 5,
        getData: () => variousMateriels.map(mat => ({
            ...formatMateriel(mat, variousMaterielsLastSituations, variousMaterielTypes, 'idVarious', 'materielType'),
            link: `/materiel/variousMateriel/${mat.id}`,
            materielCategory: MAT_DIVERS,
        })),
    }), [variousMaterielTypes, variousMateriels, variousMaterielsLastSituations])

    const telecomsValue = useMemo(() => ({
        id: 6,
        getData: () => telecoms.map(mat => ({
            ...formatMateriel(mat, telecomsLastSituations, telecomTypes, 'idTelecom', 'telecomType'),
            link: `/materiel/telecom/${mat.id}`,
            materielCategory: MAT_TELECOM,
        })),
    }), [telecomTypes, telecoms, telecomsLastSituations])

    const equipmentsValue = useMemo(() => ({
        id: 7,
        getData: () => equipments.map(mat => ({
            ...formatMateriel(mat, equipmentsLastSituations, equipmentTypes, 'idEquipment', 'equipmentType'),
            link: `/materiel/equipment/${mat.id}`,
            materielCategory: MAT_EQUIPMENT,
        })),
    }), [equipmentTypes, equipments, equipmentsLastSituations])

    const subscriptionsValue = useMemo(() => ({
        id: 8,
        getData: () => subscriptions.map(mat => ({
            ...formatMateriel(mat, subscriptionsLastSituations, subscriptionTypes, 'idSubscription', 'subscriptionType'),
            link: `/materiel/subscription/${mat.id}`,
            materielCategory: MAT_SUBSCRIPTION,
        })),
    }), [subscriptionTypes, subscriptions, subscriptionsLastSituations])

    const antennasValue = useMemo(() => ({
        id: 9,
        getData: () => antennas.map(mat => ({
            ...formatMateriel(mat, antennasLastSituations, antennaTypes, 'idAntenne', 'materielType'),
            immoNum: mat.code,
            link: `/materiel/antenna/${mat.id}`,
            materielCategory: MAT_ANTENNA,
        })),
    }), [antennas, antennasLastSituations, antennaTypes])

    const materielValue = useMemo(() => [
        centralValue,
        sensorsValue,
        powerSuppliesValue,
        simsValue,
        variousMaterielsValue,
        telecomsValue,
        equipmentsValue,
        subscriptionsValue,
        antennasValue,
    ].filter(({ id }) => hasMaterielTypeHabilitation(id)), [centralValue, sensorsValue, powerSuppliesValue, simsValue, variousMaterielsValue, telecomsValue, equipmentsValue, subscriptionsValue, antennasValue])

    const materiels = useMemo(() => {
        if (!dataload) {
            return []
        }
        const { availableMaterielTypes } = filter
        const selectedValues = availableMaterielTypes ? materielValue.filter(({ id }) => availableMaterielTypes.includes(id)) : materielValue
        return flatten(selectedValues.map(m => m.getData()))
    }, [filter.availableMaterielTypes, materielValue, dataload])

    const getHash = mat => searchAllCharacters(MAT_HEADERS_INVENTORY.map(key => mat[key]).join(''))

    const filterMateriels = (materielsToFilter) => {
        const {
            network,
            state,
            administrator,
            manufacturer,
            displayEquipment,
            searchValue,
            displayOutOfService,
            stations,
        } = filter
        const filterEquipment = !displayEquipment ? materielsToFilter.filter(({ isEquipment }) => !isEquipment) : materielsToFilter
        const filterAdministrator = administrator ? filterEquipment.filter(({ administratorId }) => administrator === administratorId) : filterEquipment
        const filterManufacturer = manufacturer ? filterAdministrator.filter(({ manufacturerId }) => manufacturer === manufacturerId) : filterAdministrator
        const filterStatusCode = hasValue(state) ? filterManufacturer.filter(({ statusCode }) => statusCode === state) : filterManufacturer
        const filterNetwork = network ? filterStatusCode.filter(({ networkCode }) => network === networkCode) : filterStatusCode
        const filterOutOfService = displayOutOfService ? filterNetwork : filterNetwork.filter(({ statusCode }) => statusCode !== 0 && statusCode !== 5 && statusCode !== 6)
        const filterStations = stations?.length ? filterOutOfService.filter(({ assignment }) => stations.includes(assignment)) : filterOutOfService
        const searchValueFormated = searchAllCharacters(searchValue)
        return searchValue ? filterStations.filter(mat => getHash(mat).includes(searchValueFormated)) : filterStations
    }

    const filteredMateriels = useMemo(() => dataload ? filterMateriels(materiels) : [], [materiels, filter, dataload])

    const formatTable = materielsToFormat => materielsToFormat.map(mat => ({
        ...mat,
        state: { value: createIconMaterialState(mat.state, mat.statusCode), sortValue: mat.state },
        assignment: {
            value: <MaterielAssignmentLink materiel={mat} />,
            sortValue: mat.assignment,
        },
        name: mat.assignment.slice(mat.assignment.indexOf('-') + 2, mat.assignment.length - 1),
        nullValue: mat.isEquipment && <Icon sx={{ fontSize: '23px' }}>devices_other</Icon>,
        materielCategory: {
            sortValue: i18n[mat.materielCategory],
            value: (
                <Stack direction='row' spacing={1} alignItems='center'>
                    {getMaterielIconValue(mat.materielCategory)}
                    <span>{i18n[mat.materielCategory]}</span>
                </Stack>
            ),
        },
    }))

    const materielsFormatted = useMemo(() => {
        if (dataload) {
            return showAll ? formatTable(filteredMateriels) : formatTable(filteredMateriels.slice(0, MAX_ELEMENTS))
        }
        return []
    }, [filteredMateriels, dataload, showAll])

    const exportData = useMemo(() => {
        const { availableMaterielTypes } = filter
        const selectedValues = availableMaterielTypes ? intersectionWith(materielValue, availableMaterielTypes, (a, b) => a.id === b) : materielValue
        const result = selectedValues.map(o => o.getData())
        const exportfilteredMateriels = filterMateriels(flatten(result))
        return exportfilteredMateriels.map(mat => ({ ...mat, materielCategory: i18n[mat.materielCategory] }))
    }, [filter.availableMaterielTypes, materielValue, filter])

    return !dataload ? (
        <ProgressCard progress={progress} />
    ) : (
        <Stack spacing={1} direction='column'>
            <InventoryFilterForm
                view={view}
                changeView={setView}
                onValidate={setFilter}
                materiels={materiels}
                materielType='INVENTORY'
            />
            <SimpleTabList
                defaultTab={LIST_PANEL}
                tabs={[
                    {
                        constant: LIST_PANEL,
                        label: i18n.materiels,
                        icon: 'router',
                    },
                    {
                        constant: STATION_LIST,
                        label: i18n.stations,
                        icon: 'place',
                    },
                    {
                        constant: MAP_PANEL,
                        label: i18n.map,
                        icon: 'map',
                    },
                ]}
                headerComponent={filteredMateriels.length > MAX_ELEMENTS && !showAll && (
                    <CardToMuchData
                        nbElements={filteredMateriels.length}
                        maxNbElements={MAX_ELEMENTS}
                        forceDisplay={() => setShowAll(true)}
                    />
                )}
                noPadding
            >
                {tab => (
                    <>
                        {tab === LIST_PANEL && <MaterialListApp materiels={materielsFormatted} exportData={exportData} />}
                        {tab === STATION_LIST && <StationListApp materiels={filteredMateriels} availableMaterielTypes={filter.availableMaterielTypes} />}
                        {tab === MAP_PANEL && <MaterielsMapComponent materiels={filteredMateriels} />}
                    </>
                )}
            </SimpleTabList>
        </Stack>
    )
}

export default InventoryApp
